/* eslint-disable no-param-reassign */
import React, {
  useContext,
  useEffect,
  useCallback,
  useState,
  Context,
} from 'react';
import { toast } from 'react-toastify';
import { observer } from 'mobx-react-lite';
import { CircularProgress } from '@material-ui/core';
import { useHistory } from 'react-router';
import { Paper, Typography, TypographyBody, Button } from './styles';
import { FormStoreContext, FormStore, resetFormStoreContext } from '../../store/FormStore';
import { instance } from '../../services/api';
import { GlobalsContext } from '../../store/Globals';
import CurlPanel from '../../components/CurlPanel';
import { fromJsonToFormContext, getForm } from './utils';

const TxFormGenerator: React.FC<{ tx: any }> = observer(({ tx }) => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const globals = useContext(GlobalsContext);
  const { checkPermission, dataTypeList } = globals;
  const context = useCallback(() => {
    return FormStoreContext([
      ...Object.keys(tx.inputs).map((prop, idx) => {
        return fromJsonToFormContext(
          (tx.inputs as any)[prop],
          tx.inputs[idx] ? tx.inputs[idx].tag : prop,
          dataTypeList,
        );
      }),
    ]);
  }, [tx, dataTypeList]);
  const Forms: FormStore = useContext(context() as Context<FormStore>);
  const { inputs } = Forms;

  const [method, setMethod] = useState<string>('');
  const [url, setURL] = useState<string>('');
  const [jsonObj, setJsonObj] = useState<any>({});

  // Reset form store context on unmount to prevent stale data
  useEffect(() => {
    return () => {
      resetFormStoreContext();
    };
  }, []);

  useEffect(() => {
    // Zero all inputs for creating
    inputs.forEach((input) => {
      input.value = null;
      input.selectorValue = null;
    });
  }, [inputs]);

  // Submit the transaction
  const submit = async (execute: boolean) => {
    setLoading(true);

    const formData = Forms.getData();

    Object.keys(formData).forEach(
      (key) =>
        (formData[key] == null || formData[key] === '') && delete formData[key],
    );

    if (execute) {
      Forms.toggleInputsDisabled();
    }

    try {
      delete formData['@key'];

      let obj = formData;

      if (tx.name.tag === 'createAssetType') {
        obj = {
          assetTypes: [
            {
              tag: obj.tag,
              label: obj.label,
              description: obj.description,
              props: obj.props,
            },
          ],
        };
      } else if (tx.name.tag === 'deleteAssetType') {
        obj = {
          assetTypes: [
            {
              tag: globals.assetList[obj.tag].tag,
              force: obj.force,
            },
          ],
        };
      }

      // Execute transaction
      if (tx.name.method === 'POST') {
        if (execute) {
          await instance.post(`/api/invoke/${tx.name.tag}`, {
            ...obj,
          });
        } else {
          setMethod('POST');
          setURL(`/api/invoke/${tx.name.tag}`);
          setJsonObj({ ...obj });
        }
      } else if (tx.name.method === 'PUT') {
        if (execute) {
          await instance.put(`/api/invoke/${tx.name.tag}`, {
            ...obj,
          });
        } else {
          setMethod('PUT');
          setURL(`/api/invoke/${tx.name.tag}`);
          setJsonObj({ ...obj });
        }
      } else {
        const request = btoa(JSON.stringify(obj));

        if (execute) {
          const txRes: any = await instance.get(
            `/api/query/${tx.name.tag}?@request=${request}`,
            { ...obj },
          );
          const objres = btoa(JSON.stringify(txRes));
          history.push(`/show/@txres?objres=${objres}`);
        } else {
          setMethod('GET');
          setURL(`/api/query/${tx.name.tag}?@request=${request}`);
          setJsonObj({});
        }
      }

      if (execute) {
        const message = `${tx.name.label} transaction commited successfully`;
        toast.success(message);
      }
    } catch (err: any) {
      const message = `Error while trying to commit transaction ${
        tx.name.label
      }: ${err.response ? err.response.data?.error : err}`;

      toast.error(message);
    } finally {
      setLoading(false);
    }

    if (execute) {
      Forms.toggleInputsDisabled();
      Forms.clearInputs();
    }
  };

  return (
    <div style={{ marginBottom: '30px' }}>
      <CurlPanel method={method} url={url} obj={jsonObj} update={submit} />
      <Paper style={{ marginTop: '10px' }}>
        <Typography>{tx.name.label}</Typography>
        <TypographyBody>{tx.name.description}</TypographyBody>
        {inputs.map((form) => getForm(form, dataTypeList))}

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </div>
        ) : (
          <Button
            onClick={() => submit(true)}
            disabled={!checkPermission(tx.name.callers)}
          >
            Submit
          </Button>
        )}
      </Paper>
      {!checkPermission(tx.name.callers) ? (
        <TypographyBody color="error">
          This transaction is not permitted to be called by this organization
        </TypographyBody>
      ) : null}
    </div>
  );
});

export default TxFormGenerator;
